(() => {
  const TABBY_SCRIPT_URL = "https://checkout.tabby.ai/tabby-promo.js";
  const CONFIG_PATTERN = (name) =>
    new RegExp(`const\\s+${name}\\s*=\\s*['\"]([^'\"]+)['\"]`);

  let retainedConstructor;
  let loaderPromise;
  let promoSequence = 0;

  function readAppConfig(block) {
    const source = Array.from(block.querySelectorAll("script"))
      .map((script) => script.textContent || "")
      .find((content) => content.includes("new TabbyPromo"));

    if (!source) return null;

    const read = (name) => source.match(CONFIG_PATTERN(name))?.[1];
    const currency = read("currency") || window.Shopify?.currency?.active;
    const publicKey = read("publicKey");

    if (!currency || !publicKey) return null;

    return {
      currency,
      lang: read("lang") || document.documentElement.lang || "en",
      merchantCode:
        currency === "AED"
          ? "default"
          : currency === "SAR"
            ? "ksa"
            : currency === "KWD"
              ? "KW"
              : undefined,
      publicKey,
    };
  }

  function getSelectedVariant(variantSelector) {
    let variantData;

    try {
      variantData = JSON.parse(
        variantSelector.querySelector('[type="application/json"]')?.textContent || "[]",
      );
    } catch (error) {
      console.warn("Unable to read product variants for Tabby Promo.", error);
      return null;
    }

    const selectedOptions = Array.from(
      variantSelector.querySelectorAll("select, fieldset"),
      (control) =>
        control.tagName === "SELECT"
          ? control.value
          : control.querySelector('input[type="radio"]:checked')?.value,
    ).filter(Boolean);

    return variantData.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index]),
    );
  }

  function getTabbyConstructor() {
    if (typeof window.TabbyPromo === "function") {
      retainedConstructor = window.TabbyPromo;
    }

    return retainedConstructor;
  }

  function loadTabbyLibrary() {
    if (loaderPromise) return loaderPromise;

    loaderPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = TABBY_SCRIPT_URL;
      script.async = true;
      script.onload = () => {
        const TabbyPromo = getTabbyConstructor();
        if (TabbyPromo) {
          resolve(TabbyPromo);
        } else {
          reject(new Error("Tabby Promo loaded without exposing its constructor."));
        }
      };
      script.onerror = () => reject(new Error("Tabby Promo failed to load."));
      document.body.appendChild(script);
    }).finally(() => {
      loaderPromise = null;
    });

    return loaderPromise;
  }

  function waitForRender(promo) {
    return new Promise((resolve) => {
      window.setTimeout(() => resolve(promo.childElementCount > 0), 450);
    });
  }

  function getPromoSelector(promo) {
    if (document.querySelectorAll("#TabbyPromo").length > 1) {
      promoSequence += 1;
      promo.id = `TabbyPromo-${promoSequence}`;
    }

    return `#${CSS.escape(promo.id)}`;
  }

  async function initializePromo(promo, options, forceLibraryReload = false) {
    let TabbyPromo = forceLibraryReload ? null : getTabbyConstructor();
    if (!TabbyPromo) TabbyPromo = await loadTabbyLibrary();

    promo.replaceChildren();
    new TabbyPromo(options);
    return waitForRender(promo);
  }

  async function renderForVariant(variantSelector) {
    const productInfo = variantSelector.closest(".product__info-container");
    const block = productInfo?.querySelector(".product__app-block");
    const promo = block?.querySelector("#TabbyPromo");
    const variant = getSelectedVariant(variantSelector);
    const config = block ? readAppConfig(block) : null;

    if (!productInfo || !promo || !variant?.price || !config) return;

    const renderToken = (productInfo.lotusTabbyRenderToken || 0) + 1;
    productInfo.lotusTabbyRenderToken = renderToken;

    const options = {
      selector: getPromoSelector(promo),
      currency: config.currency,
      price: variant.price / 100,
      lang: config.lang,
      publicKey: config.publicKey,
    };

    if (config.merchantCode) options.merchantCode = config.merchantCode;

    try {
      let rendered = await initializePromo(promo, options);
      if (productInfo.lotusTabbyRenderToken !== renderToken) return;

      if (!rendered) {
        retainedConstructor = null;
        rendered = await initializePromo(promo, options, true);
      }

      block.toggleAttribute("data-tabby-unavailable", !rendered);
    } catch (error) {
      block.setAttribute("data-tabby-unavailable", "");
      console.warn("Unable to refresh Tabby Promo for the selected variant.", error);
    }
  }

  document.addEventListener("change", (event) => {
    const variantSelector = event.target.closest("variant-selects, variant-radios");
    if (!variantSelector) return;

    const productInfo = variantSelector.closest(".product__info-container");
    window.clearTimeout(productInfo?.lotusTabbyRefreshTimer);

    const timer = window.setTimeout(() => renderForVariant(variantSelector), 150);
    if (productInfo) productInfo.lotusTabbyRefreshTimer = timer;
  });
})();
